/**
 * One card (PRD 10.1): what it asks for, what it waits on, and the buttons
 * for what this person may do with it.
 *
 * What is drawn is `cardFor()`'s answer from the worker, so another
 * department's card arrives as its title and state and nothing else, and a
 * PII value arrives only for somebody who can work the card. The buttons are
 * a courtesy: every one of them goes through `act()` in the organisation's
 * object, which refuses in its own words, and the refusal is shown as it came.
 *
 * Every action answers with the whole board, read back from what was stored,
 * and the screen redraws from that -- never from what it hoped had happened.
 */

import { useState, type FormEvent } from "react";
import {
  CHOICE_SEPARATOR,
  choicesOf,
  isRedacted,
  canOversee,
  type ActRequest,
  type BoardCard,
  type BoardResponse,
  type CardField,
  type CardFull,
  type InstanceSummary,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { boardOf } from "../lib/router.js";
import { accessText, anchorLabel, dayText, dueText, listText, siteTimeText, whenText } from "../lib/words.js";
import { Link, Loading, Problem, SlaPill, StatePill, useLoad } from "./bits.js";

const OPEN = ["unblocked", "in_progress", "blocked"];

export function CardPage({ id, cardKey }: { id: string; cardKey: string }) {
  const { api } = useOrg();
  const { data: board, setData, error } = useLoad(() => api.board(id, cardKey), [api, id, cardKey]);

  if (!board && !error) return <Loading title="Card" />;
  if (!board) {
    return (
      <section className="page">
        <div className="panel">
          <h1 className="panel-title">Card</h1>
          <Problem>{error}</Problem>
        </div>
      </section>
    );
  }

  const entry = board.cards.find((candidate) => candidate.card.key === cardKey);
  if (!entry) {
    return (
      <section className="page">
        <div className="panel">
          <h1 className="panel-title">Card</h1>
          <Problem>That card is not part of this one.</Problem>
        </div>
      </section>
    );
  }

  return <CardScreen board={board} entry={entry} onBoard={setData} />;
}

function CardScreen({
  board,
  entry,
  onBoard,
}: {
  board: BoardResponse;
  entry: BoardCard;
  onBoard: (board: BoardResponse) => void;
}) {
  return (
    <section className="page">
      <HireStrip board={board} />
      <CardSheet board={board} entry={entry} onBoard={onBoard} />
    </section>
  );
}

/**
 * The card beside My work, on a screen wide enough for both: the same sheet
 * as the card's own page, loaded from its board. `onChange` is told after
 * every action, so the list beside it can move the card to where it now is.
 */
export function CardPane({ id, cardKey, onChange }: { id: string; cardKey: string; onChange: () => void }) {
  const { api } = useOrg();
  const { data, setData, error } = useLoad(() => api.board(id, cardKey), [api, id, cardKey]);
  // Until the new board arrives the last one is still held; it is not this card's.
  const board = data?.instance.id === id ? data : null;
  const entry = board?.cards.find((candidate) => candidate.card.key === cardKey);

  if (!entry) {
    return (
      <div className="panel card-sheet" aria-busy={!board && !error}>
        {board || error ? (
          <Problem>{error ?? "That card is not part of this one."}</Problem>
        ) : (
          <p className="muted">Loading…</p>
        )}
      </div>
    );
  }
  return (
    <div className="pane-stack">
      <HireStrip board={board!} />
      <CardSheet
        key={cardKey}
        board={board!}
        entry={entry}
        pane
        onBoard={(next) => {
          setData(next);
          onChange();
        }}
      />
    </div>
  );
}

/**
 * Whose work this is, as a way to their board for whoever may open one; for
 * anybody else it is only the name, since the board is not theirs to reach.
 */
function BoardLink({ instance, className }: { instance: InstanceSummary; className?: string }) {
  const { org } = useOrg();
  if (!canOversee(org)) return <span className={className}>{instance.employeeName}</span>;
  return (
    <Link to={boardOf(instance.id, instance.kind)} className={className}>
      {instance.employeeName}
    </Link>
  );
}

/** Two letters for a name: "Jordan Reyes" is JR. Decoration beside the name, never instead of it. */
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]!.toUpperCase())
    .slice(0, 2)
    .join("");

const PARTS = [
  { name: "done", label: "done", states: ["completed", "skipped"] },
  { name: "open", label: "open", states: ["unblocked", "in_progress"] },
  { name: "blocked", label: "blocked", states: ["blocked"] },
  { name: "waiting", label: "not yet open", states: ["not_started"] },
] as const;

/**
 * Whose work this card is part of, and how far all of it has come: one bar
 * in four parts, with the counts in words beside it. Notices are left out;
 * they finish themselves. Drawn in SVG because the CSP allows no inline
 * styles, and a rectangle's width is an attribute.
 */
export function HireStrip({ board }: { board: BoardResponse }) {
  const instance = board.instance;
  const tasks = board.cards.filter((entry) => entry.card.kind === "task");
  const counts = PARTS.map((part) => ({
    ...part,
    count: tasks.filter((entry) => (part.states as readonly string[]).includes(entry.card.state)).length,
  }));
  let x = 0;
  return (
    <div className="panel hire">
      <div className="hire-top">
        <span className="avatar" aria-hidden="true">
          {initials(instance.employeeName)}
        </span>
        <BoardLink instance={instance} className="hire-name" />
        <span className="muted">
          {instance.siteName} ·{" "}
          {instance.kind === "offboarding" ? anchorLabel(instance).toLowerCase() : "starts"}{" "}
          {dayText(instance.anchorDate)}
        </span>
      </div>
      <svg
        className="hire-bar"
        viewBox={`0 0 ${Math.max(tasks.length, 1)} 1`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {counts.map((part) => {
          const rect = (
            <rect key={part.name} className={`seg-${part.name}`} x={x} width={part.count} height={1} />
          );
          x += part.count;
          return rect;
        })}
      </svg>
      <p className="hire-counts">
        {counts.map((part) => (
          <span key={part.name}>
            <span className={`hire-dot seg-${part.name}`} aria-hidden="true" />
            {part.count} {part.label}
          </span>
        ))}
      </p>
    </div>
  );
}

/** The card itself: its title and state, then everything this person may see and do with it. */
function CardSheet({
  board,
  entry,
  onBoard,
  pane,
}: {
  board: BoardResponse;
  entry: BoardCard;
  onBoard: (board: BoardResponse) => void;
  pane?: boolean;
}) {
  const { card } = entry;
  const instance = board.instance;
  const Title = pane ? "h2" : "h1";
  return (
    <div className="panel card-sheet">
      <div className="card-top">
        <Title className="panel-title">
          {card.kind === "notify" ? <span className="card-kind">Notice · </span> : null}
          {card.title}
        </Title>
        <StatePill state={card.state} />
      </div>
      {card.full ? (
        <>
          <FullCard board={board} entry={entry} card={card} onBoard={onBoard} />
          <Comments board={board} card={card} onBoard={onBoard} />
          <Poke board={board} card={card} onBoard={onBoard} />
        </>
      ) : (
        <>
          <p className="card-meta">
            <span className="chip">{card.department}</span>
            <span>
              For <BoardLink instance={instance} />
            </span>
          </p>
          <p className="muted">
            This card is {card.department}'s. You can see where it is; its details are for the people who work
            it.
          </p>
          <Poke board={board} card={card} onBoard={onBoard} />
        </>
      )}
    </div>
  );
}

/**
 * Who hears next, as one sentence. Up to three cards are named; past that a
 * list of every title stops being read, so it says how many open and which
 * departments are told (a departure's first card opens ten).
 */
/**
 * A card that revokes access: the time it was asked for, on the site's
 * clock, beside the time it was done -- what a client's offboarding form
 * compares by hand for the access SLA.
 */
function AccessLine({ card }: { card: CardFull }) {
  const { requestedAt, timeZone } = card.access!;
  const doneAt = card.state === "completed" ? card.finishedAt : null;
  const against = card.state === "skipped" ? null : accessText(requestedAt, doneAt, Date.now());
  return (
    <p className={`notice-line${against?.late ? " warn" : ""}`}>
      Access asked to go {siteTimeText(requestedAt, timeZone)}
      {doneAt !== null ? `; gone ${siteTimeText(doneAt, timeZone)}` : ""}
      {against ? `${doneAt !== null ? "," : ";"} ${against.text}` : ""}.
    </p>
  );
}

function nextText(opens: string[], board: BoardResponse): string {
  const next = opens.flatMap((key) => {
    const found = board.cards.find((other) => other.card.key === key)?.card;
    return found ? [found] : [];
  });
  if (next.length > 3) {
    const departments = [...new Set(next.map((card) => card.department))];
    return `When this is done, ${next.length} cards open and ${listText(departments)} ${departments.length === 1 ? "is" : "are"} told.`;
  }
  return `When this is done, ${listText(
    next.map((card) =>
      card.kind === "notify"
        ? `${card.department} gets the notice "${card.title}"`
        : `${card.department} is told "${card.title}" can begin`,
    ),
  )}.`;
}

function FullCard({
  board,
  entry,
  card,
  onBoard,
}: {
  board: BoardResponse;
  entry: BoardCard;
  card: CardFull;
  onBoard: (board: BoardResponse) => void;
}) {
  const { api, nameOf, refreshUnread } = useOrg();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [asking, setAsking] = useState<null | "block" | "skip" | "reassign">(null);
  const [reviewedBy, setReviewedBy] = useState("");

  const titleOf = (key: string) => board.cards.find((other) => other.card.key === key)?.card.title ?? key;
  const open = OPEN.includes(card.state);
  const finished = card.state === "completed" || card.state === "skipped";
  const works = card.canWork && card.kind === "task";

  async function run(action: ActRequest): Promise<boolean> {
    setBusy(true);
    setProblem(null);
    try {
      onBoard(await api.act(board.instance.id, card.key, action));
      void refreshUnread();
      return true;
    } catch (error) {
      setProblem(sentence(error));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const draftOf = (field: CardField) => drafts[field.key] ?? field.value ?? "";
  const changed = (field: CardField) =>
    drafts[field.key] !== undefined && drafts[field.key] !== (field.value ?? "");

  async function finish() {
    const values = Object.fromEntries(
      card.fields.filter((field) => changed(field)).map((field) => [field.key, draftOf(field)]),
    );
    if (
      await run({
        type: "complete",
        values,
        ...(card.compliance && board.instance.hipaa && reviewedBy.trim() ? { reviewedBy } : {}),
      })
    ) {
      setDrafts({});
    }
  }

  return (
    <div className="card-body">
      <p className="card-meta">
        <span className="chip">{card.department}</span>
        <span>
          For <BoardLink instance={board.instance} />
        </span>
        <span>{dueText(card)}</span>
        <SlaPill sla={entry.sla} />
        {card.timeCritical ? <span className="pill s-danger">Time-critical</span> : null}
        {card.compliance ? <span className="chip">Compliance</span> : null}
      </p>
      <p className="card-meta">
        <span>
          Held by <strong className="ink">{nameOf(card.assigneeId)}</strong>
          {card.assigneeId === null ? `: anyone in ${card.department} can take it` : null}
        </span>
        {card.backupId ? <span>Backup {nameOf(card.backupId)}</span> : null}
      </p>
      {entry.flagged ? (
        <p className="notice-line warn">
          Whoever held this has been disabled. A manager, HR or an admin should hand it to somebody.
        </p>
      ) : null}
      {card.access ? <AccessLine card={card} /> : null}
      {card.compressed ? (
        <p className="notice-line warn">Moved up: the day the template asked for had already passed.</p>
      ) : null}
      {card.escalation >= 2 ? (
        <p className="notice-line danger">
          {card.escalation === 3 ? "In HR's critical queue." : `${card.department}'s manager has been told.`}
        </p>
      ) : null}

      {card.description ? <p className="card-description">{card.description}</p> : null}
      {card.link ? (
        <p>
          <a className="button button-quiet" href={card.link} target="_blank" rel="noreferrer noopener">
            Open the tool for this
          </a>
        </p>
      ) : null}

      {card.waitingOn.length > 0 ? (
        <div className="callout">
          <strong>Waiting on</strong>
          <ul>
            {card.waitingOn.map((key) => (
              <li key={key}>
                <Link to={{ name: "card", id: board.instance.id, key }}>{titleOf(key)}</Link>
              </li>
            ))}
          </ul>
          <p className="muted">You will get a notice when it is your turn.</p>
        </div>
      ) : null}
      {open && card.softWaitingOn.length > 0 ? (
        <p className="notice-line warn">
          Best done after {card.softWaitingOn.map(titleOf).join(", ")}, which{" "}
          {card.softWaitingOn.length === 1 ? "is" : "are"} not finished yet.
        </p>
      ) : null}
      {card.blockedReason ? (
        <p className="notice-line danger">
          <strong>Blocked:</strong> {card.blockedReason}
        </p>
      ) : null}
      {card.skipReason ? (
        <p className="notice-line">
          <strong>Skipped:</strong> {card.skipReason}
        </p>
      ) : null}

      {card.subtasks.length > 0 ? (
        <fieldset className="section">
          <legend className="section-title">Checklist</legend>
          {card.subtasks.map((subtask, index) => (
            <label key={subtask.text} className="check">
              <input
                type="checkbox"
                checked={subtask.done}
                disabled={busy || !works || !open}
                onChange={(event) => void run({ type: "tick", index, done: event.target.checked })}
              />
              <span>{subtask.text}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <Notes card={card} writable={works && open} busy={busy} onRun={run} />

      {card.inputs.length > 0 ? (
        <div className="section">
          <h2 className="section-title">From earlier cards</h2>
          <dl className="values">
            {card.inputs.map((input) => (
              <div key={`${input.from}.${input.label}`}>
                <dt>{input.label}</dt>
                <dd>
                  {input.value.state === "ready" ? (
                    choicesOf(input.value.value).join(", ")
                  ) : input.value.state === "pending" ? (
                    <span className="muted">Pending {input.fromTitle}</span>
                  ) : input.value.state === "hidden" ? (
                    <span className="muted">Private: shown only to whoever works this card</span>
                  ) : (
                    <span className="muted">Not recorded</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      {card.fields.length > 0 ? (
        <div className="section">
          <h2 className="section-title">What this records</h2>
          {card.fields.map((field) => (
            <div key={field.key} className="field-row">
              {field.multiple && field.options && !field.hidden ? (
                <fieldset className="field field-choices" disabled={busy || !works || !open}>
                  <legend>
                    {field.label}
                    {field.required ? " (required)" : ""}
                    {field.pii ? <span className="chip chip-inline">Private</span> : null}
                  </legend>
                  <span className="muted">Choose any that apply.</span>
                  <div className="checks">
                    {field.options.map((option) => {
                      const chosen = choicesOf(draftOf(field));
                      return (
                        <label key={option} className="check">
                          <input
                            type="checkbox"
                            checked={chosen.includes(option)}
                            onChange={(event) => {
                              const next = field.options!.filter((other) =>
                                other === option ? event.target.checked : chosen.includes(other),
                              );
                              setDrafts({ ...drafts, [field.key]: next.join(CHOICE_SEPARATOR) });
                            }}
                          />
                          <span>{option}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                <label className="field">
                  <span>
                    {field.label}
                    {field.required ? " (required)" : ""}
                    {field.pii ? <span className="chip chip-inline">Private</span> : null}
                  </span>
                  {field.hidden ? (
                    <span className="muted field-hidden">Shown only to whoever works this card.</span>
                  ) : field.value !== null && isRedacted(field.value) ? (
                    // Erased under a request (PRD 11.2): said, not offered for editing.
                    <span className="muted field-hidden">{field.value}</span>
                  ) : field.options ? (
                    <select
                      value={draftOf(field)}
                      disabled={busy || !works || !open}
                      onChange={(event) => setDrafts({ ...drafts, [field.key]: event.target.value })}
                    >
                      <option value="">Choose…</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={draftOf(field)}
                      disabled={busy || !works || !open}
                      autoComplete="off"
                      onChange={(event) => setDrafts({ ...drafts, [field.key]: event.target.value })}
                    />
                  )}
                  {field.issued ? (
                    <span className="muted">
                      Issued: <strong>{field.issued.value}</strong> (written down on "{field.issued.from}")
                      {draftOf(field).trim() !== "" &&
                      draftOf(field).trim().toLowerCase() !== field.issued.value.toLowerCase() ? (
                        <span className="pill s-danger chip-inline">Does not match what was issued</span>
                      ) : null}
                    </span>
                  ) : null}
                </label>
              )}
              {changed(field) ? (
                <button
                  className="button button-quiet"
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    if (await run({ type: "set", field: field.key, value: draftOf(field) })) {
                      const { [field.key]: _saved, ...rest } = drafts;
                      setDrafts(rest);
                    }
                  }}
                >
                  Save
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {card.signoffs.length > 0 ? (
        <div className="section">
          <h2 className="section-title">Sign-offs</h2>
          <ul className="signoffs">
            {card.signoffs.map((signoff) => {
              const mine = card.canSignFor.includes(signoff.department);
              return (
                <li key={signoff.department}>
                  <span>
                    <strong>{signoff.department}</strong>{" "}
                    {signoff.by ? (
                      <span className="muted">
                        signed by {nameOf(signoff.by)}, {whenText(signoff.at!)}
                      </span>
                    ) : (
                      <span className="muted">waiting</span>
                    )}
                  </span>
                  {mine && open ? (
                    <button
                      className="button button-quiet"
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void run({ type: signoff.by ? "unsign" : "signoff", department: signoff.department })
                      }
                    >
                      {signoff.by ? "Withdraw" : `Sign for ${signoff.department}`}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {card.finishedAt ? (
        <p className="muted">
          {card.state === "skipped" ? "Skipped" : "Done"} by {nameOf(card.finishedBy)},{" "}
          {whenText(card.finishedAt)}.
        </p>
      ) : null}

      {open && card.kind === "task" && card.opens.length > 0 ? (
        <p className="next-line">
          <svg viewBox="0 0 16 16" aria-hidden="true" className="next-icon">
            <circle cx="8" cy="8" r="6.5" />
            <path d="M8 7.2v4M8 4.9v.1" />
          </svg>
          <span>{nextText(card.opens, board)}</span>
        </p>
      ) : null}

      <Problem>{problem}</Problem>

      {!finished && card.kind === "task" ? (
        <div className="actions">
          {/* Asked for exactly where the engine will demand it: a compliance card on a HIPAA site. */}
          {works && open && card.compliance && board.instance.hipaa && card.state !== "blocked" ? (
            <label className="field">
              <span>Reviewed by (required on a HIPAA site)</span>
              <input
                value={reviewedBy}
                onChange={(event) => setReviewedBy(event.target.value)}
                placeholder="Who reviewed it"
              />
            </label>
          ) : null}
          <div className="action-row">
            {works && open && card.state !== "blocked" ? (
              <button className="button" type="button" disabled={busy} onClick={() => void finish()}>
                Done
              </button>
            ) : null}
            {works && card.state === "unblocked" ? (
              <button
                className="button button-quiet"
                type="button"
                disabled={busy}
                onClick={() => void run({ type: "start" })}
              >
                Start
              </button>
            ) : null}
            {works && card.state === "blocked" ? (
              <button
                className="button"
                type="button"
                disabled={busy}
                onClick={() => void run({ type: "resume" })}
              >
                Pick it back up
              </button>
            ) : null}
            {works && open && card.state !== "blocked" ? (
              <button
                className="button button-quiet"
                type="button"
                disabled={busy}
                onClick={() => setAsking("block")}
              >
                Something's in the way
              </button>
            ) : null}
            {works && card.escalation < 3 && card.state === "blocked" ? (
              <button
                className="button button-quiet"
                type="button"
                disabled={busy}
                onClick={() => void run({ type: "escalate" })}
              >
                Escalate
              </button>
            ) : null}
            {board.canDirect ? (
              <>
                <button
                  className="button button-quiet"
                  type="button"
                  disabled={busy}
                  onClick={() => setAsking("reassign")}
                >
                  Hand to somebody else
                </button>
                <button
                  className="button button-quiet"
                  type="button"
                  disabled={busy}
                  onClick={() => setAsking("skip")}
                >
                  Skip
                </button>
              </>
            ) : null}
          </div>
          {asking === "block" || asking === "skip" ? (
            <ReasonForm
              kind={asking}
              busy={busy}
              onCancel={() => setAsking(null)}
              onSubmit={async (reason) => {
                if (await run({ type: asking, reason })) setAsking(null);
              }}
            />
          ) : null}
          {asking === "reassign" ? (
            <Reassign
              card={card}
              busy={busy}
              onCancel={() => setAsking(null)}
              onSubmit={async (assigneeId, backupId) => {
                if (await run({ type: "reassign", assigneeId, backupId })) setAsking(null);
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ReasonForm({
  kind,
  busy,
  onCancel,
  onSubmit,
}: {
  kind: "block" | "skip";
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onSubmit(reason);
  };
  return (
    <form className="ask" onSubmit={submit}>
      <label className="field">
        <span>{kind === "block" ? "What is in the way?" : "Why skip it? It goes in the audit log."}</span>
        <textarea
          value={reason}
          rows={3}
          onChange={(event) => setReason(event.target.value)}
          placeholder={
            kind === "block" ? "The laptop has not been delivered yet." : "Rehire: the badge is still active."
          }
        />
      </label>
      <div className="action-row">
        <button className="button" type="submit" disabled={busy || !reason.trim()}>
          {kind === "block" ? "Flag it as blocked" : "Skip this card"}
        </button>
        <button className="button button-quiet" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Reassign({
  card,
  busy,
  onCancel,
  onSubmit,
}: {
  card: CardFull;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (assigneeId: string | null, backupId: string | null) => Promise<void>;
}) {
  const { people } = useOrg();
  const [assignee, setAssignee] = useState(card.assigneeId ?? "");
  const [backup, setBackup] = useState(card.backupId ?? "");
  const active = people.filter((person) => person.active);
  // Their own department first: that is nearly always who it goes to.
  const sorted = [
    ...active.filter((person) => person.department === card.department),
    ...active.filter((person) => person.department !== card.department),
  ];
  const label = (id: string) => {
    const person = active.find((candidate) => candidate.id === id)!;
    return `${person.name || person.email}${person.department ? ` (${person.department})` : ""}`;
  };

  return (
    <form
      className="ask"
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(assignee || null, backup || null);
      }}
    >
      <label className="field">
        <span>Held by</span>
        <select value={assignee} onChange={(event) => setAssignee(event.target.value)}>
          <option value="">Nobody: anyone in {card.department}</option>
          {sorted.map((person) => (
            <option key={person.id} value={person.id}>
              {label(person.id)}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Backup</span>
        <select value={backup} onChange={(event) => setBackup(event.target.value)}>
          <option value="">None</option>
          {sorted.map((person) => (
            <option key={person.id} value={person.id}>
              {label(person.id)}
            </option>
          ))}
        </select>
      </label>
      <div className="action-row">
        <button className="button" type="submit" disabled={busy}>
          Hand it over
        </button>
        <button className="button button-quiet" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * What has been said on a card, and a place to say more. The question about
 * a card is asked here rather than by email: the card's owner and anyone
 * already in the thread are told, and so is anybody named. Naming is by
 * choosing from the list, so what is sent is who was meant and not a guess
 * at a spelling. Nothing is edited or removed once sent.
 */
function Comments({
  board,
  card,
  onBoard,
}: {
  board: BoardResponse;
  card: CardFull;
  onBoard: (board: BoardResponse) => void;
}) {
  const { api, people, nameOf } = useOrg();
  const [text, setText] = useState("");
  const [named, setNamed] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const choices = people.filter((person) => person.active && person.role !== "compliance");

  async function send(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      // Only those still written in the text: deleting "@Ada" un-names her.
      const mentions = Object.entries(named)
        .filter(([, name]) => text.includes(`@${name}`))
        .map(([id]) => id);
      onBoard(await api.act(board.instance.id, card.key, { type: "comment", text, mentions }));
      setText("");
      setNamed({});
    } catch (error) {
      setProblem(sentence(error));
    } finally {
      setBusy(false);
    }
  }

  function mention(id: string) {
    const person = choices.find((candidate) => candidate.id === id);
    if (!person) return;
    setNamed({ ...named, [id]: person.name });
    setText((before) => `${before}${before && !/s$/.test(before) ? " " : ""}@${person.name} `);
  }

  return (
    <div className="section">
      <h2 className="section-title">Comments</h2>
      {card.comments.length === 0 ? (
        <p className="muted">
          Nothing said yet. Ask here instead of by email; whoever holds the card is told.
        </p>
      ) : (
        <ul className="comments-list">
          {card.comments.map((entry) => (
            <li key={`${entry.at}-${entry.by}`}>
              <p className="comment-head">
                <strong>{nameOf(entry.by)}</strong> <span className="muted">{whenText(entry.at)}</span>
              </p>
              <p className="comment-text">{entry.text}</p>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={send}>
        <label className="field">
          <span>Add a comment</span>
          <textarea value={text} rows={3} onChange={(event) => setText(event.target.value)} />
        </label>
        <div className="action-row">
          <button className="button" type="submit" disabled={busy || !text.trim()}>
            Comment
          </button>
          <label className="field field-inline">
            <span className="visually-hidden">Mention somebody</span>
            <select value="" onChange={(event) => mention(event.target.value)}>
              <option value="">Mention somebody…</option>
              {choices.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </form>
      <Problem>{problem}</Problem>
    </div>
  );
}

/**
 * "Somebody is waiting on this" (PRD 7.5): a nudge to whoever holds the card,
 * in a fixed sentence, once a day. Shown to anybody who may send one, which
 * is why it sits outside the full card: a poke is most often sent by another
 * department, who sees only the summary.
 */
function Poke({
  board,
  card,
  onBoard,
}: {
  board: BoardResponse;
  card: BoardCard["card"];
  onBoard: (board: BoardResponse) => void;
}) {
  const { api } = useOrg();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  if (!card.canPoke) return null;

  async function send() {
    setBusy(true);
    setProblem(null);
    try {
      onBoard(await api.act(board.instance.id, card.key, { type: "poke" }));
      setSent(true);
    } catch (error) {
      setProblem(sentence(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="section">
      <div className="action-row">
        <button
          className="button button-quiet"
          type="button"
          disabled={busy || sent}
          onClick={() => void send()}
        >
          {sent ? "Checked in" : "I'm waiting on this"}
        </button>
      </div>
      <p className="muted">Whoever holds it is told. You can check in once a day.</p>
      <Problem>{problem}</Problem>
    </div>
  );
}

/**
 * Handover notes (PRD 9.4): one per domain, each with a link to whatever the
 * leaver wrote elsewhere. Writing a note on a domain that already has one
 * replaces it, so editing is "choose it, change it, save".
 */
function Notes({
  card,
  writable,
  busy,
  onRun,
}: {
  card: CardFull;
  writable: boolean;
  busy: boolean;
  onRun: (action: ActRequest) => Promise<boolean>;
}) {
  const { nameOf } = useOrg();
  const [domain, setDomain] = useState("");
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  if (card.notes === null) return null;

  async function save(event: FormEvent) {
    event.preventDefault();
    if (await onRun({ type: "note", domain, text, link })) {
      setDomain("");
      setText("");
      setLink("");
    }
  }

  return (
    <div className="section">
      <h2 className="section-title">Notes</h2>
      {card.notes.length === 0 ? <p className="muted">Nothing written yet.</p> : null}
      <ul className="notes-list">
        {card.notes.map((note) => (
          <li key={note.domain}>
            <strong>{note.domain}</strong>
            <p className="card-description">{note.text}</p>
            {note.link ? (
              <p>
                <a href={note.link} target="_blank" rel="noreferrer noopener">
                  {note.link}
                </a>
              </p>
            ) : null}
            <p className="muted">By {nameOf(note.by)}</p>
            {writable ? (
              <div className="action-row">
                <button
                  className="button button-quiet"
                  type="button"
                  onClick={() => {
                    setDomain(note.domain);
                    setText(note.text);
                    setLink(note.link ?? "");
                  }}
                >
                  Edit
                </button>
                <button
                  className="button button-quiet"
                  type="button"
                  disabled={busy}
                  onClick={() => void onRun({ type: "unnote", domain: note.domain })}
                >
                  Remove
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {writable ? (
        <form onSubmit={save}>
          <label className="field">
            <span>What it is about</span>
            <input
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
              placeholder="Month-end billing"
            />
          </label>
          <label className="field">
            <span>What needs to be known</span>
            <textarea value={text} rows={4} onChange={(event) => setText(event.target.value)} />
          </label>
          <label className="field">
            <span>Link to a document (optional)</span>
            <input
              type="url"
              value={link}
              onChange={(event) => setLink(event.target.value)}
              placeholder="https://"
            />
          </label>
          <div className="action-row">
            <button className="button" type="submit" disabled={busy || !domain.trim() || !text.trim()}>
              Save the note
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
